/**
 * User permission checking hook
 *
 * This hook provides role and permission checking functionality based on the
 * authenticated user's actual permissions, not organization membership.
 *
 * Use this for authorization decisions throughout the application.
 * Works with both regular AuthProvider and AdminAuthProvider contexts.
 *
 * Uses useCallback to ensure stable function references, which prevents
 * unnecessary re-renders in components that depend on these functions.
 */

import { useCallback, useContext, useMemo } from 'react';
import { AuthContext } from '../context/auth.context';
import { AdminAuthContext } from '../context/AdminAuthContext';

export function useUserPermissions() {
  // Get both contexts (hooks must be called unconditionally)
  const authContext = useContext(AuthContext);
  const adminAuthContext = useContext(AdminAuthContext);

  // Use admin context if available, otherwise fall back to regular auth
  const user = adminAuthContext?.admin || authContext?.user || null;
  const permissions = useMemo(() => user?.permissions || [], [user?.permissions]);
  const roles = useMemo(() => user?.roles || [], [user?.roles]);

  /**
   * Check if user has a specific permission
   * Memoized to provide stable reference - only changes when permissions change
   */
  const hasPermission = useCallback(
    (permission: string): boolean => permissions.includes(permission),
    [permissions],
  );

  /**
   * Check if user has any of the specified permissions
   * Memoized to provide stable reference - only changes when permissions change
   */
  const hasAnyPermission = useCallback(
    (...perms: string[]): boolean => perms.some((perm) => permissions.includes(perm)),
    [permissions],
  );

  /**
   * Check if user has all of the specified permissions
   * Memoized to provide stable reference - only changes when permissions change
   */
  const hasAllPermissions = useCallback(
    (...perms: string[]): boolean => perms.every((perm) => permissions.includes(perm)),
    [permissions],
  );

  /**
   * Check if user is a system administrator
   * System admins have access to admin portal and global resources
   * Memoized to provide stable reference - only changes when permissions change
   */
  const isSystemAdmin = useCallback(
    (): boolean => permissions.includes('system:admin'),
    [permissions],
  );

  /**
   * Check if user can manage organization settings
   * Memoized to provide stable reference - only changes when permissions change
   */
  const canManageOrganization = useCallback(
    (): boolean => permissions.includes('organizations:update'),
    [permissions],
  );

  /**
   * Check if user can manage team members (update or delete)
   * Memoized to provide stable reference - only changes when permissions change
   */
  const canManageMembers = useCallback(
    (): boolean => permissions.includes('users:update') || permissions.includes('users:delete'),
    [permissions],
  );

  /**
   * Check if user can approve pending membership requests
   * Memoized to provide stable reference - only changes when permissions change
   */
  const canApproveMembers = useCallback(
    (): boolean => permissions.includes('memberships:approve'),
    [permissions],
  );

  /**
   * Check if user has any organization role
   * Memoized to provide stable reference - only changes when roles change
   */
  const hasOrgRole = useCallback(
    (): boolean => roles.some((role) => role.startsWith('org:')),
    [roles],
  );

  return {
    permissions,
    roles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSystemAdmin,
    canManageOrganization,
    canManageMembers,
    canApproveMembers,
    hasOrgRole,
  };
}
